import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import { ArrowUpRight, ArrowRight, MapPin, Award, TrendingUp, Users, Beaker, Plane, ClipboardCheck, Music, GraduationCap, HandshakeIcon, Sparkles, Building2, Hotel, Presentation, Globe2, Syringe, Microscope, Handshake, BookOpen, Star, X, ZoomIn } from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import { IMAGES } from "@/constants/site";

export default function About() {
  const location = useLocation();
  const [lightbox, setLightbox] = useState(null);

  const openLightbox = useCallback((src, alt) => setLightbox({ src, alt }), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") closeLightbox(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [lightbox, closeLightbox]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // Wait for content to render before scrolling
      const timeout = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 200);
      return () => clearTimeout(timeout);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.hash]);

  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="The Agency & Portfolio — Case Studies from Recent Programs"
        description="Explore Magee Enterprises' portfolio of employee-recognition programs, association conferences, film destination management, and hybrid vaccine summits — case studies with real numbers, methods, and outcomes."
        path="/about"
        image={IMAGES.gala}
        keywords="event case studies, event portfolio, event agency case studies, corporate incentive program, association conference case study, hybrid event case study"
      />
      {/* PORTFOLIO — Case Study: Employee Recognition */}
      <section id="portfolio" className="relative py-24 md:py-32 border-t border-[var(--border)] overflow-hidden scroll-mt-28">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[500px] h-[500px] -top-20 -left-20 opacity-40" />
        <div className="brand-bloom brand-bloom-warm w-[380px] h-[380px] bottom-0 -right-24 opacity-30" />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Portfolio · Case Study
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Employee Recognition — at{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    national scale.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                A multi-city incentive program engineered to feel identical in every
                room — from Manhattan to Honolulu.
              </p>
            </div>
          </Reveal>

          {/* Hero card */}
          <Reveal delay={0.1}>
            <div className="card-white overflow-hidden relative">
              <span className="ribbon-badge">Verizon Excellence Awards</span>
              <div className="grid grid-cols-12">
                <div className="col-span-12 lg:col-span-7 relative aspect-[16/10] lg:aspect-auto lg:min-h-[560px] overflow-hidden">
                  <img
                    src="/portfolio/case01-incentives-verizon.jpg"
                    alt="Verizon Excellence Awards gala"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#050A14]/60 via-[#050A14]/10 to-transparent" />
                  <div className="absolute top-6 left-6">
                    <span className="chip !bg-white/90 !text-[var(--text)] !border-white/50">
                      <span className="chip-dot" /> Incentives Program
                    </span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-5 p-8 md:p-12 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-[var(--border)]">
                  <div>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Client · Verizon Communications
                    </p>
                    <p className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Category · Incentives &amp; Recognition
                    </p>
                    <p className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] flex items-start gap-2">
                      <MapPin size={12} className="mt-0.5 shrink-0 text-[var(--accent)]" />
                      <span>NY · Boston · California · Texas · Chicago · DC · Puerto Rico · Hawaii</span>
                    </p>

                    <p className="mt-8 text-[var(--text)] text-[16px] leading-relaxed">
                      A nationwide employee-recognition program produced across
                      dozens of cities on tight, overlapping schedules — every
                      event visually and experientially identical so no honoree
                      would feel their program was second best.
                    </p>

                    <div className="mt-6 grid grid-cols-3 gap-3">
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">32</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Events</div>
                      </div>
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">8k+</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Honorees</div>
                      </div>
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">+100%</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Nominations</div>
                      </div>
                    </div>
                  </div>
                  <Link to="/contact" className="btn-primary mt-10">
                    Start a Program <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Narrative */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-8 md:gap-12">
            <Reveal delay={0.05} className="col-span-12 md:col-span-6">
              <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                Staging a spectacular event is a feat in itself.
              </h3>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                Producing dozens of events in different cities on the same
                timeline is harder still — a logistical minefield resolved with
                obsessive planning, deep partner networks, and a house-brand of
                repeatable execution.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="col-span-12 md:col-span-6">
              <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                Consistency as the design principle.
              </h3>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                Identical decor, floral, lighting, entertainment, and audio-visual
                across every city. Signature marketing collateral. A rebuilt
                nomination platform that doubled participation — from a few
                thousand to more than six thousand submissions in a single cycle.
              </p>
            </Reveal>
          </div>

          {/* Gallery */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-3 md:gap-4">
            {[
              "/portfolio/case01-atrium-gala.jpg",
              "/portfolio/case01-verizon-awards-stage.jpg",
              "/portfolio/case01-chiavari-sunflower.jpg",
              "/portfolio/case01-showgirl-performers.jpg",
            ].map((src, i) => (
              <Reveal key={src} delay={i * 0.06} className="col-span-6 md:col-span-3">
                <button
                  type="button"
                  onClick={() => openLightbox(src, `Excellence Awards moment ${i + 1}`)}
                  data-testid={`gallery-case01-${i + 1}`}
                  className="group relative aspect-square overflow-hidden rounded-2xl w-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                  aria-label={`Enlarge Excellence Awards moment ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`Excellence Awards moment ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                  />
                  <span className="absolute top-3 left-3 chip !bg-white/85 !text-[var(--text)] !border-white/50 !py-1 !px-2 !text-[9px]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/45 text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10">
                      <ZoomIn size={20} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>

          {/* Gallery credit */}
          <Reveal delay={0.1}>
            <p
              data-testid="gallery-case01-credit"
              className="mt-4 text-xs md:text-sm italic text-[var(--text-muted)] tracking-wide"
            >
              Photography courtesy of Magee Enterprises client archives
            </p>
          </Reveal>

          {/* AS FEATURED IN — Special Events Magazine · June 2004 */}
          <Reveal delay={0.15}>
            <div className="press-feature mt-16 md:mt-24">
              {/* Ambient stage light */}
              <div className="press-feature__spot" aria-hidden />
              <div className="press-feature__grain" aria-hidden />
              <div className="press-feature__glow-a" aria-hidden />
              <div className="press-feature__glow-b" aria-hidden />

              <div className="press-feature__grid">
                {/* LEFT — Magazine cover on a floating 3D frame */}
                <div className="press-feature__cover-wrap">
                  <div className="press-feature__stage" aria-hidden />
                  <div className="press-feature__cover" role="img" aria-label="Special Events Magazine cover — June 2004">
                    <img
                      src="/press/special-events-mag-june-2004.webp"
                      alt=""
                      className="press-feature__img"
                    />
                    {/* soft crop overlays to hide the redacted subtitle patch */}
                    <div className="press-feature__crop-bottom" aria-hidden />
                    <div className="press-feature__crop-shine" aria-hidden />
                  </div>
                  {/* Corkboard pin + ribbon */}
                  <span className="press-feature__ribbon">
                    <Star size={11} className="opacity-90" />
                    As Featured In
                  </span>
                </div>

                {/* RIGHT — Editorial-style copy */}
                <div className="press-feature__copy">
                  <span className="press-feature__eyebrow">
                    <BookOpen size={12} /> Press · National Trade Publication
                  </span>
                  <h3 className="press-feature__title">
                    <span className="press-feature__title-serif">Special Events</span>
                    <span className="press-feature__title-italic">Magazine</span>
                  </h3>
                  <div className="press-feature__meta">
                    <span className="press-feature__meta-rule" />
                    <span className="press-feature__meta-text">
                      Cover story · <em>June&nbsp;2004</em> · Corporate Events
                    </span>
                  </div>
                  <p className="press-feature__quote">
                    &ldquo;The&nbsp;Verizon Excellence Awards program set a new bar
                    for national-scale employee recognition — engineered so every
                    honoree, in every city, felt they were at the flagship
                    event.&rdquo;
                  </p>
                  <div className="press-feature__tags">
                    <span className="press-feature__tag">Cover Feature</span>
                    <span className="press-feature__tag">Corporate Events</span>
                    <span className="press-feature__tag">National Program</span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Recognition + Key take-aways */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-6 md:gap-10">
            <Reveal className="col-span-12 md:col-span-5">
              <div className="card-white p-8">
                <span className="chip chip-warm">
                  <Award size={12} /> Recognition
                </span>
                <p className="mt-6 text-[var(--text)] leading-relaxed">
                  Featured in <em>Special Events Magazine</em>. Voted <strong>Corporate
                  Planner of the Year</strong> by Event Solutions Magazine and{" "}
                  <strong>Young &amp; Inspiring Event Professional in Industry</strong>{" "}
                  (2005) by Special Events Magazine — recognition earned for
                  the discipline behind the Excellence Awards program.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="col-span-12 md:col-span-7">
              <div className="card-white p-8">
                <span className="chip">
                  <TrendingUp size={12} /> Key take-aways
                </span>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: Users, t: "Hire and lean on strong partners." },
                    { icon: TrendingUp, t: "Write the plan — then stay flexible." },
                    { icon: Award, t: "Give every stakeholder clear purpose." },
                    { icon: MapPin, t: "Enjoy the work. Have fun on the floor." },
                  ].map((k) => (
                    <li key={k.t} className="flex items-start gap-4">
                      <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center">
                        <k.icon size={16} />
                      </div>
                      <span className="text-[var(--text)] text-[15px] leading-relaxed">
                        {k.t}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PORTFOLIO — Case Study 2: BioTech Clinical Investigator Meetings */}
      <section id="case02" className="relative py-24 md:py-32 border-t border-[var(--border)] overflow-hidden bg-[var(--surface)] scroll-mt-28">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -bottom-20 -left-20 opacity-40" />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Portfolio · Case Study 02
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  BioTech investigator meetings —{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    three cities, nine months.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                A CMP-led investigator meeting program orchestrated for a life-sciences
                client — precise, compliant, and repeatable across continents.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card-white overflow-hidden relative">
              <span className="ribbon-badge">CMP · Investigator Meetings</span>
              <div className="grid grid-cols-12">
                <div className="col-span-12 lg:col-span-5 p-8 md:p-12 flex flex-col justify-between order-2 lg:order-1 border-t lg:border-t-0 lg:border-r border-[var(--border)]">
                  <div>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Client · BioTech Pharma Company
                    </p>
                    <p className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Category · Conference &amp; Exhibits
                    </p>
                    <p className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] flex items-start gap-2">
                      <MapPin size={12} className="mt-0.5 shrink-0 text-[var(--accent)]" />
                      <span>Paris, France · Sydney, Australia · Chicago, IL</span>
                    </p>

                    <p className="mt-8 text-[var(--text)] text-[16px] leading-relaxed">
                      Three global investigator meetings, produced over a nine-month
                      window, delivered around dedicated late-night and early-morning
                      workstreams to keep the client focused on the science.
                    </p>

                    <div className="mt-6 grid grid-cols-3 gap-3">
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">3</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Meetings</div>
                      </div>
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">9mo</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Timeline</div>
                      </div>
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">CMP</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Led</div>
                      </div>
                    </div>
                  </div>
                  <Link to="/contact" className="btn-primary mt-10">
                    Start a Program <ArrowRight size={14} />
                  </Link>
                </div>

                <div className="col-span-12 lg:col-span-7 relative aspect-[16/10] lg:aspect-auto lg:min-h-[560px] overflow-hidden order-1 lg:order-2">
                  <img
                    src="/portfolio/hero_paris.jpg"
                    alt="Paris investigator meeting host city"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-bl from-[#050A14]/50 via-[#050A14]/10 to-transparent" />
                  <div className="absolute top-6 right-6">
                    <span className="chip !bg-white/90 !text-[var(--text)] !border-white/50">
                      <span className="chip-dot" /> Conference &amp; Exhibits
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Narrative */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-8 md:gap-12">
            <Reveal delay={0.05} className="col-span-12 md:col-span-6">
              <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                Exceeding the brief. Lifting the ROI.
              </h3>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                Multi-continent investigator meetings demand disciplined planning,
                a team willing to work around the clock, and clients who trust the
                agency to translate priorities into flawless execution.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="col-span-12 md:col-span-6">
              <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                Proactive thinking reduces stress.
              </h3>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                By anchoring on the client&rsquo;s top priorities and success factors
                up front, we compress tight deadlines into confident timelines —
                and hand the science team a program they never have to worry about.
              </p>
            </Reveal>
          </div>

          {/* Scope grid */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {[
              { icon: ClipboardCheck, t: "Full conference project management" },
              { icon: Plane, t: "Meet &amp; greet ground transportation" },
              { icon: Users, t: "Attendee registration logistics" },
              { icon: Beaker, t: "Exhibition training &amp; management" },
              { icon: MapPin, t: "On-site leadership &amp; execution" },
              { icon: TrendingUp, t: "Concise, decision-grade reporting" },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="card-white p-6 flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center">
                    <s.icon size={16} />
                  </div>
                  <div>
                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                      /{String(i + 1).padStart(2, "0")}
                    </div>
                    <div
                      className="font-editorial text-[15px] leading-tight mt-1"
                      dangerouslySetInnerHTML={{ __html: s.t }}
                    />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Gallery */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-3 md:gap-4">
            {[
              "/portfolio/g01_forte.jpg",
              "/portfolio/g02_banquet.jpg",
              "/portfolio/g03_hendricks.jpg",
              "/portfolio/g04_registration.jpg",
            ].map((src, i) => (
              <Reveal key={src} delay={i * 0.06} className="col-span-6 md:col-span-3">
                <button
                  type="button"
                  onClick={() => openLightbox(src, `Investigator meeting moment ${i + 1}`)}
                  data-testid={`gallery-case02-${i + 1}`}
                  className="group relative aspect-square overflow-hidden rounded-2xl w-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                  aria-label={`Enlarge investigator meeting moment ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`Investigator meeting moment ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                  />
                  <span className="absolute top-3 left-3 chip !bg-white/85 !text-[var(--text)] !border-white/50 !py-1 !px-2 !text-[9px]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/45 text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10">
                      <ZoomIn size={20} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>

          {/* Gallery credit */}
          <Reveal delay={0.1}>
            <p
              data-testid="gallery-case02-credit"
              className="mt-4 text-xs md:text-sm italic text-[var(--text-muted)] tracking-wide"
            >
              Multiple venues · 2024&ndash;2025 · Photography courtesy of Magee Enterprises client archives
            </p>
          </Reveal>

          {/* Assurance */}
          <Reveal delay={0.15}>
            <div className="mt-16 card-white p-8 md:p-10 flex items-start gap-5">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center">
                <Award size={18} />
              </div>
              <p className="text-[var(--text)] text-lg leading-relaxed">
                Engaging a Certified Meeting Professional (CMP) from Magee
                Enterprises means your program is delivered against the industry&rsquo;s
                highest standards of professionalism, compliance, and craft.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PORTFOLIO — Case Study 03: Presidential Inauguration Celebration */}
      <section className="relative py-24 md:py-32 border-t border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-warm w-[420px] h-[420px] -top-24 -right-24 opacity-35" />
        <div className="brand-bloom brand-bloom-blue w-[460px] h-[460px] bottom-0 -left-24 opacity-30" />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Portfolio · Case Study 03
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Welcoming a new president —{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    a one-day jubilee.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                A community-wide celebration produced on a compressed timeline for a
                growing, multicultural college campus.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card-white overflow-hidden relative">
              <span className="ribbon-badge">University · Inauguration</span>
              <div className="grid grid-cols-12">
                <div className="col-span-12 lg:col-span-7 relative aspect-[16/10] lg:aspect-auto lg:min-h-[560px] overflow-hidden">
                  <img
                    src="/portfolio/case03-pillar-college-group.jpg"
                    alt="Pillar College presidential celebration"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#050A14]/55 via-[#050A14]/10 to-transparent" />
                  <div className="absolute top-6 left-6">
                    <span className="chip !bg-white/90 !text-[var(--text)] !border-white/50">
                      <GraduationCap size={12} /> University Events
                    </span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-5 p-8 md:p-12 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-[var(--border)]">
                  <div>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Client · Pillar College
                    </p>
                    <p className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Category · University Events · Inauguration
                    </p>
                    <p className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] flex items-start gap-2">
                      <MapPin size={12} className="mt-0.5 shrink-0 text-[var(--accent)]" />
                      <span>New Jersey, USA</span>
                    </p>

                    <p className="mt-8 text-[var(--text)] text-[16px] leading-relaxed">
                      A one-day celebration marking the inauguration of the
                      college&rsquo;s 2022 president — bringing together community
                      leaders, alumni, and students for a program that honored the
                      school&rsquo;s heritage and its future.
                    </p>

                    <div className="mt-6 grid grid-cols-3 gap-3">
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">1 day</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Jubilee</div>
                      </div>
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">3</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Audiences</div>
                      </div>
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">Full</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Production</div>
                      </div>
                    </div>
                  </div>
                  <Link to="/contact" className="btn-primary mt-10">
                    Plan a Celebration <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Narrative */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-8 md:gap-12">
            <Reveal delay={0.05} className="col-span-12 md:col-span-6">
              <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                Quick planning, decisive execution.
              </h3>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                A short runway to organize a community-wide welcome for a fast-growing,
                multicultural college — an event that had to feel unmistakably
                intentional in every detail.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="col-span-12 md:col-span-6">
              <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                Delivered with integrity, passion, and ethics.
              </h3>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                From decor and show production to speakers, food &amp; beverage, and
                registration — every element was owned end-to-end so the
                college could focus entirely on welcoming its new leader.
              </p>
            </Reveal>
          </div>

          {/* Scope grid */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { icon: Sparkles, t: "Full decor planning and execution" },
              { icon: Music, t: "On-site show producer and stage cues" },
              { icon: ClipboardCheck, t: "Supplier logistics management" },
              { icon: Users, t: "Exceptional handling of every on-site element" },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="card-white p-6 flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center">
                    <s.icon size={16} />
                  </div>
                  <div>
                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                      /{String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="font-editorial text-[15px] leading-tight mt-1">
                      {s.t}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Gallery */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-3 md:gap-4">
            {[
              "/portfolio/case03-hooding-ceremony.jpg",
              "/portfolio/case03-education-inspiration-banner.jpg",
              "/portfolio/case03-inauguration-sign.jpg",
            ].map((src, i) => (
              <Reveal key={src} delay={i * 0.06} className="col-span-12 md:col-span-4">
                <button
                  type="button"
                  onClick={() => openLightbox(src, `Presidential celebration moment ${i + 1}`)}
                  data-testid={`gallery-case03-${i + 1}`}
                  className="group relative aspect-[4/3] overflow-hidden rounded-2xl w-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                  aria-label={`Enlarge presidential celebration moment ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`Presidential celebration moment ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                  />
                  <span className="absolute top-3 left-3 chip !bg-white/85 !text-[var(--text)] !border-white/50 !py-1 !px-2 !text-[9px]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/45 text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10">
                      <ZoomIn size={20} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>

          {/* Gallery credit */}
          <Reveal delay={0.1}>
            <p
              data-testid="gallery-case03-credit"
              className="mt-4 text-xs md:text-sm italic text-[var(--text-muted)] tracking-wide"
            >
              Photography courtesy of Magee Enterprises client archives
            </p>
          </Reveal>

          {/* Pull quote */}
          <Reveal delay={0.15}>
            <div className="mt-16 card-white p-8 md:p-12">
              <span className="font-editorial text-6xl leading-none text-[var(--accent)]">&ldquo;</span>
              <p className="mt-3 font-editorial text-2xl md:text-4xl leading-tight tracking-[-0.02em] text-balance max-w-3xl">
                A delight to produce — a memorable celebration built to be
                remembered for years to come.
              </p>
              <div className="mt-6 font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                — Sir Dion Magee, CEO
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MID-PAGE CTA — Plan. Lead. Inspire. */}
      <section className="py-24 md:py-40 border-t border-[var(--text)]/10 bg-[var(--surface-alt)]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <h3 className="font-editorial text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
              Plan. Lead. Inspire.
              <br />
              <em className="text-[var(--accent)]">Every time.</em>
            </h3>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              className="mt-12 inline-flex items-center gap-3 border border-[var(--text)]/20 hover:border-[var(--accent)] hover:bg-[var(--accent)] transition-all px-8 py-4 font-mono-brand text-[11px] uppercase tracking-[0.22em]"
            >
              Start a conversation
              <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PORTFOLIO — Case Study 04: Novartis · AABB Conference */}
      <section className="relative py-24 md:py-32 border-t border-[var(--border)] overflow-hidden bg-[var(--surface)]">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -top-24 -left-24 opacity-35" />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Portfolio · Case Study 04
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Booth &amp; attendee management —{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    three years running.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                An end-to-end conference-participation program run for a global
                pharma client across three consecutive annual meetings.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card-white overflow-hidden relative">
              <span className="ribbon-badge">Corporate · Trade Show</span>
              <div className="grid grid-cols-12">
                <div className="col-span-12 lg:col-span-5 p-8 md:p-12 flex flex-col justify-between order-2 lg:order-1 border-t lg:border-t-0 lg:border-r border-[var(--border)]">
                  <div>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Client · Novartis
                    </p>
                    <p className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Category · Corporate · Conference &amp; Trade Show
                    </p>
                    <p className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] flex items-start gap-2">
                      <MapPin size={12} className="mt-0.5 shrink-0 text-[var(--accent)]" />
                      <span>Mexico · Virtual · USA</span>
                    </p>

                    <p className="mt-8 text-[var(--text)] text-[16px] leading-relaxed">
                      Owning Novartis&rsquo; participation at the AABB Annual
                      Meeting — the global gathering for blood and
                      biotherapies — across three consecutive years, blending
                      on-site booth operations with full attendee-journey
                      management.
                    </p>

                    <div className="mt-6 grid grid-cols-3 gap-3">
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">600</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Attendees</div>
                      </div>
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">3 yrs</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Retained</div>
                      </div>
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">3</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Regions</div>
                      </div>
                    </div>
                  </div>
                  <Link to="/contact" className="btn-primary mt-10">
                    Manage a Conference <ArrowRight size={14} />
                  </Link>
                </div>

                <div className="col-span-12 lg:col-span-7 relative aspect-[16/10] lg:aspect-auto lg:min-h-[560px] overflow-hidden order-1 lg:order-2">
                  <img
                    src="https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?auto=format&fit=crop&w=1600&q=80"
                    alt="Corporate booth and trade show"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-bl from-[#050A14]/50 via-[#050A14]/10 to-transparent" />
                  <div className="absolute top-6 right-6">
                    <span className="chip !bg-white/90 !text-[var(--text)] !border-white/50">
                      <Building2 size={12} /> AABB Annual Meeting
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Narrative */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-8 md:gap-12">
            <Reveal delay={0.05} className="col-span-12 md:col-span-6">
              <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                One team, one point of accountability.
              </h3>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                From travel and housing to booth build and off-site activations,
                the client engaged a single agency to shoulder the operational
                complexity of AABB — so their internal team could focus on the
                science and the customers on the floor.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="col-span-12 md:col-span-6">
              <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                Delivered under third-party governance.
              </h3>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                Executed under strict third-party contractual terms — the kind of
                compliance-heavy delivery that life-sciences programs demand. Every
                touchpoint documented, every SLA met.
              </p>
            </Reveal>
          </div>

          {/* Scope grid */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { icon: Plane, t: "Travel &amp; hotel logistics for 600 attendees" },
              { icon: Hotel, t: "National housing-system support" },
              { icon: Presentation, t: "Booth setup &amp; on-floor management" },
              { icon: Globe2, t: "Off-site events &amp; DMC coordination" },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="card-white p-6 flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center">
                    <s.icon size={16} />
                  </div>
                  <div>
                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                      /{String(i + 1).padStart(2, "0")}
                    </div>
                    <div
                      className="font-editorial text-[15px] leading-tight mt-1"
                      dangerouslySetInnerHTML={{ __html: s.t }}
                    />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Gallery */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-3 md:gap-4">
            {[
              "/portfolio/case04-aabb-2018-banner.jpg",
              "/portfolio/case04-blue-banquet-dna.jpg",
              "/portfolio/case04-panel-audience.webp",
              "/portfolio/case04-logistics-ballroom.webp",
            ].map((src, i) => (
              <Reveal key={src} delay={i * 0.06} className="col-span-6 md:col-span-3">
                <button
                  type="button"
                  onClick={() => openLightbox(src, `Novartis AABB booth ${i + 1}`)}
                  data-testid={`gallery-case04-${i + 1}`}
                  className="group relative aspect-square overflow-hidden rounded-2xl w-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                  aria-label={`Enlarge Novartis AABB booth image ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`Novartis AABB booth ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                  />
                  <span className="absolute top-3 left-3 chip !bg-white/85 !text-[var(--text)] !border-white/50 !py-1 !px-2 !text-[9px]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/45 text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10">
                      <ZoomIn size={20} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>

          {/* Gallery credit */}
          <Reveal delay={0.1}>
            <p
              data-testid="gallery-case04-credit"
              className="mt-4 text-xs md:text-sm italic text-[var(--text-muted)] tracking-wide"
            >
              Photography courtesy of Magee Enterprises client archives
            </p>
          </Reveal>
        </div>
      </section>

      {/* PORTFOLIO — Case Study 05: Vaccines Conference */}
      <section className="relative py-24 md:py-32 border-t border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[460px] h-[460px] -top-24 -right-24 opacity-35" />
        <div className="brand-bloom brand-bloom-cyan w-[420px] h-[420px] bottom-0 -left-20 opacity-30" />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Portfolio · Case Study 05
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Vaccines conference —{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    where science convenes.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                A global scientific gathering for the vaccine research community —
                produced with CMP-grade discipline from sponsorship to on-site show.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card-white overflow-hidden relative">
              <span className="ribbon-badge">Scientific · Conference</span>
              <div className="grid grid-cols-12">
                <div className="col-span-12 lg:col-span-7 relative aspect-[16/10] lg:aspect-auto lg:min-h-[560px] overflow-hidden">
                  <img
                    src="/portfolio/case05-vaccines-conference-banner.jpg"
                    alt="Vaccines scientific conference hall"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#050A14]/55 via-[#050A14]/10 to-transparent" />
                  <div className="absolute top-6 left-6">
                    <span className="chip !bg-white/90 !text-[var(--text)] !border-white/50">
                      <Microscope size={12} /> Global Scientific Convening
                    </span>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-5 p-8 md:p-12 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-[var(--border)]">
                  <div>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Client · ISDN Vaccines Company
                    </p>
                    <p className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                      Category · Conference &amp; Exhibits
                    </p>
                    <p className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] flex items-start gap-2">
                      <MapPin size={12} className="mt-0.5 shrink-0 text-[var(--accent)]" />
                      <span>San Diego, California</span>
                    </p>

                    <p className="mt-8 text-[var(--text)] text-[16px] leading-relaxed">
                      A conference dedicated to advancing vaccine research —
                      bringing leading investigators together to share
                      cutting-edge findings, spark collaboration, and translate
                      discovery into practice.
                    </p>

                    <div className="mt-6 grid grid-cols-3 gap-3">
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]"><Syringe size={22} className="inline" /></div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Vaccines</div>
                      </div>
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">Global</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Speakers</div>
                      </div>
                      <div className="card-soft p-3 text-center">
                        <div className="font-editorial text-2xl text-[var(--accent)]">CMP</div>
                        <div className="mt-1 font-mono-brand text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)]">Led</div>
                      </div>
                    </div>
                  </div>
                  <Link to="/contact" className="btn-primary mt-10">
                    Convene the Field <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Narrative */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-8 md:gap-12">
            <Reveal delay={0.05} className="col-span-12 md:col-span-6">
              <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                Breakthroughs need a room to be shared in.
              </h3>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                As medical technology accelerates, the fastest gains often come from
                gathering the right minds in the right room. The agency was engaged
                to produce that room — end to end.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="col-span-12 md:col-span-6">
              <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                Planning care as carefully as the science.
              </h3>
              <p className="mt-6 text-[var(--text-muted)] text-lg leading-relaxed">
                Medical and educational programs are a privilege to produce —
                every registration touch, every sponsorship conversation, every
                on-site cue treated with the same rigor the research demands.
              </p>
            </Reveal>
          </div>

          {/* Scope grid */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {[
              { icon: ClipboardCheck, t: "Full conference project management" },
              { icon: Handshake, t: "Sponsorship outreach and on-boarding" },
              { icon: Users, t: "Attendee registration logistics" },
              { icon: Presentation, t: "Exhibition training and management" },
              { icon: MapPin, t: "On-site leadership and execution" },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="card-white p-6 flex items-start gap-4 h-full">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center">
                    <s.icon size={16} />
                  </div>
                  <div>
                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                      /{String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="font-editorial text-[15px] leading-tight mt-1">
                      {s.t}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Gallery */}
          <div className="mt-16 md:mt-20 grid grid-cols-12 gap-3 md:gap-4">
            {[
              "/portfolio/case05-buffet-tulips.webp",
              "/portfolio/case05-canapes-buffet.webp",
              "/portfolio/case05-blue-ballroom-dna.png",
              "/portfolio/case05-architecture-summit.webp",
            ].map((src, i) => (
              <Reveal key={src} delay={i * 0.06} className="col-span-12 md:col-span-6">
                <button
                  type="button"
                  onClick={() => openLightbox(src, `Vaccines conference moment ${i + 1}`)}
                  data-testid={`gallery-case05-${i + 1}`}
                  className="group relative aspect-[4/3] overflow-hidden rounded-2xl w-full block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                  aria-label={`Enlarge vaccines conference moment ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`Vaccines conference moment ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                  />
                  <span className="absolute top-3 left-3 chip !bg-white/85 !text-[var(--text)] !border-white/50 !py-1 !px-2 !text-[9px]">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                  <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/45 text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10">
                      <ZoomIn size={20} strokeWidth={2.2} />
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>

          {/* Gallery credit */}
          <Reveal delay={0.1}>
            <p
              data-testid="gallery-case05-credit"
              className="mt-4 text-xs md:text-sm italic text-[var(--text-muted)] tracking-wide"
            >
              Photography courtesy of Magee Enterprises client archives
            </p>
          </Reveal>

          {/* CMP assurance */}
          <Reveal delay={0.15}>
            <div className="mt-16 card-white p-8 md:p-10 flex items-start gap-5">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center">
                <Award size={18} />
              </div>
              <p className="text-[var(--text)] text-lg leading-relaxed">
                Engaging a Certified Meeting Professional from Magee Enterprises
                means your program is delivered against the industry&rsquo;s
                highest standards of excellence and professionalism.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LIGHTBOX — click gallery photos to enlarge */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Enlarged image: ${lightbox.alt}`}
          onClick={closeLightbox}
          data-testid="portfolio-lightbox"
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 md:p-8 animate-in fade-in duration-300"
        >
          <button
            type="button"
            onClick={closeLightbox}
            aria-label="Close enlarged image"
            data-testid="portfolio-lightbox-close"
            className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white flex items-center justify-center transition-colors backdrop-blur"
          >
            <X size={22} />
          </button>
          <div
            className="relative max-w-[92vw] max-h-[88vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-[0_30px_100px_-20px_rgba(0,0,0,0.7)]"
            />
            {lightbox.alt && (
              <p className="mt-3 text-center text-white/70 text-sm font-mono-brand uppercase tracking-[0.2em]">
                {lightbox.alt}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
